import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GuestInputError, parseGuestSeats } from "../../shared/guests";
import { api } from "../api";
import { Banner, messageOf } from "./ui";

type Seat = { name: string; table: string };

type GuestPage = {
  venue_name: string;
  guest_count: number;
  menu_label: string;
  guests: Seat[];
  submitted_at: string | null;
};

const emptySeat = (): Seat => ({ name: "", table: "" });

export function GuestListPage() {
  const { token } = useParams();
  const [page, setPage] = useState<GuestPage | null>(null);
  const [rows, setRows] = useState<Seat[]>([emptySeat()]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api<GuestPage>(`/public/guests/${encodeURIComponent(token)}`)
      .then((result) => {
        setPage(result);
        setRows(result.guests.length ? result.guests : [emptySeat()]);
        if (result.submitted_at) setNote("Η λίστα έχει καταχωριστεί. Μπορείτε να τη διορθώσετε και να την ξαναστείλετε.");
      })
      .catch((reason: unknown) => setError(messageOf(reason)));
  }, [token]);

  function setRow(index: number, partial: Partial<Seat>) {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...partial } : row)));
    setNote(null);
  }

  async function submit() {
    if (!token || !page || busy) return;
    let guests: Seat[];
    try {
      guests = parseGuestSeats(rows);
    } catch (reason: unknown) {
      setError(reason instanceof GuestInputError ? reason.message : messageOf(reason));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ count: number; message: string }>(`/public/guests/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ guests }),
      });
      setPage({ ...page, guests, submitted_at: new Date().toISOString() });
      setRows(guests);
      setNote(result.message);
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setBusy(false);
    }
  }

  const house = page?.venue_name || "την εκδήλωση";
  return (
    <div className="public guest-list">
      <p className="meta">Λίστα καλεσμένων</p>
      <h1>{page?.venue_name || "Καλεσμένοι"}</h1>
      <p className="lede">Γράψτε το όνομα κάθε καλεσμένου και δίπλα τον αριθμό του τραπεζιού για {house}.</p>
      {page ? <p className="meta">Καλεσμένοι: {page.guest_count}. Μενού: {page.menu_label}.</p> : null}
      <Banner error={error} note={note} />
      {page ? (
        <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          <div className="guest-rows">
            {rows.map((row, index) => (
              <div className="guest-row" key={index}>
                <label>Όνομα
                  <input value={row.name} onChange={(event) => setRow(index, { name: event.target.value })} />
                </label>
                <label>Τραπέζι
                  <input inputMode="numeric" value={row.table} onChange={(event) => setRow(index, { table: event.target.value })} />
                </label>
                {rows.length > 1 ? <button type="button" className="secondary" onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}>Αφαίρεση</button> : null}
              </div>
            ))}
          </div>
          <div className="actions">
            <button type="button" className="secondary" onClick={() => setRows((current) => [...current, emptySeat()])}>+ Καλεσμένος</button>
            <button type="submit" disabled={busy}>{busy ? "Αποστολή…" : "Αποστολή"}</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
